import { FormEvent, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { LuArrowRight, LuLock } from 'react-icons/lu';
import PhoneInput, { type Country } from 'react-phone-number-input';
import { toast } from 'react-toastify';

import GetStartedCountrySelect from './GetStartedCountrySelect';
import {
  formatPhoneForSubmit,
  getPhoneError,
  getWorkEmailError,
} from '../../lib/leadFormValidation';
import { submitLeadForm } from '../../lib/submitLeadForm';
import SbrIcon from './SbrIcon';

type Props = {
  show: boolean;
  onHide: () => void;
};

type FormState = {
  fullName: string;
  workEmail: string;
  company: string;
  jobTitle: string;
  phone: string;
  hiringGoals: string;
  marketingOptIn: boolean;
};

type FieldErrors = {
  workEmail?: string;
  phone?: string;
};

const initialFormState: FormState = {
  fullName: '',
  workEmail: '',
  company: '',
  jobTitle: '',
  phone: '',
  hiringGoals: '',
  marketingOptIn: false,
};

const hiringGoalsPlaceholder =
  'What roles are you hiring for?\nWhat are your biggest hiring challenges?\nWhat outcomes are you looking to achieve?';

export default function GetStartedLeadModal({ show, onHide }: Props) {
  const [form, setForm] = useState<FormState>(initialFormState);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [phoneCountry, setPhoneCountry] = useState<Country>('US');
  const [submitting, setSubmitting] = useState(false);

  const resetAndClose = () => {
    setForm(initialFormState);
    setFieldErrors({});
    setPhoneCountry('US');
    onHide();
  };

  const handlePhoneChange = (value?: string) => {
    const next = value ?? '';
    setForm((current) => ({ ...current, phone: next }));
    setFieldErrors((current) => ({ ...current, phone: getPhoneError(next) ?? undefined }));
  };

  const handleWorkEmailChange = (value: string) => {
    setForm((current) => ({ ...current, workEmail: value }));
    if (fieldErrors.workEmail) {
      setFieldErrors((current) => ({
        ...current,
        workEmail: getWorkEmailError(value) ?? undefined,
      }));
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const fullName = form.fullName.trim();
    const workEmail = form.workEmail.trim();
    const company = form.company.trim();
    const jobTitle = form.jobTitle.trim();
    const hiringGoals = form.hiringGoals.trim();
    const workEmailError = getWorkEmailError(workEmail);
    const phoneError = getPhoneError(form.phone);

    if (!fullName || !workEmail || !company || !jobTitle || !hiringGoals || !form.phone.trim()) {
      toast.error('Please fill in all required fields.');
      return;
    }

    if (workEmailError || phoneError) {
      setFieldErrors({
        workEmail: workEmailError ?? undefined,
        phone: phoneError ?? undefined,
      });
      toast.error(workEmailError || phoneError || 'Please fix the highlighted fields.');
      return;
    }

    setFieldErrors({});
    setSubmitting(true);

    try {
      await submitLeadForm({
        fullName,
        workEmail,
        company,
        jobTitle,
        phone: formatPhoneForSubmit(form.phone),
        hiringGoals,
        marketingOptIn: form.marketingOptIn,
      });

      toast.success('Thanks! Our team will reach out shortly.');
      resetAndClose();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Something went wrong. Please try again.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const updateField =
    <K extends keyof FormState>(key: K) =>
    (value: FormState[K]) => {
      setForm((current) => ({ ...current, [key]: value }));
    };

  return (
    <Modal
      show={show}
      onHide={resetAndClose}
      centered
      size="lg"
      className="get-started-modal"
      aria-labelledby="get-started-modal-title"
    >
      <Modal.Header closeButton className="get-started-modal__header border-0 pb-0">
        <Modal.Title id="get-started-modal-title" className="get-started-modal__title">
          About You
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="get-started-modal__body pt-2 pb-4 px-4 px-lg-5">
        <Form onSubmit={handleSubmit}>
          <div className="get-started-modal__fields">
            <Form.Group controlId="lead-full-name">
              <Form.Label className="get-started-modal__label">
                Full Name<span aria-hidden>*</span>
              </Form.Label>
              <Form.Control
                className="get-started-modal__input"
                type="text"
                required
                autoComplete="name"
                placeholder="Enter your full name"
                value={form.fullName}
                onChange={(event) => updateField('fullName')(event.target.value)}
              />
            </Form.Group>

            <Form.Group controlId="lead-work-email">
              <Form.Label className="get-started-modal__label">
                Work Email<span aria-hidden>*</span>
              </Form.Label>
              <Form.Control
                className="get-started-modal__input"
                type="email"
                required
                autoComplete="email"
                placeholder="you@yourcompany.com"
                value={form.workEmail}
                isInvalid={Boolean(fieldErrors.workEmail)}
                onChange={(event) => handleWorkEmailChange(event.target.value)}
                onBlur={(event) =>
                  setFieldErrors((current) => ({
                    ...current,
                    workEmail: getWorkEmailError(event.target.value) ?? undefined,
                  }))
                }
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors.workEmail}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="lead-company">
              <Form.Label className="get-started-modal__label">
                Company<span aria-hidden>*</span>
              </Form.Label>
              <Form.Control
                className="get-started-modal__input"
                type="text"
                required
                autoComplete="organization"
                placeholder="Enter your company name"
                value={form.company}
                onChange={(event) => updateField('company')(event.target.value)}
              />
            </Form.Group>

            <Form.Group controlId="lead-job-title">
              <Form.Label className="get-started-modal__label">
                Job Title<span aria-hidden>*</span>
              </Form.Label>
              <Form.Control
                className="get-started-modal__input"
                type="text"
                required
                autoComplete="organization-title"
                placeholder="Your job title"
                value={form.jobTitle}
                onChange={(event) => updateField('jobTitle')(event.target.value)}
              />
            </Form.Group>

            <Form.Group controlId="lead-phone">
              <Form.Label className="get-started-modal__label">
                Phone Number<span aria-hidden>*</span>
              </Form.Label>
              <PhoneInput
                international
                countryCallingCodeEditable={false}
                defaultCountry="US"
                country={phoneCountry}
                countrySelectComponent={GetStartedCountrySelect}
                onCountryChange={(country) => setPhoneCountry(country ?? 'US')}
                value={form.phone}
                onChange={handlePhoneChange}
                onBlur={() =>
                  setFieldErrors((current) => ({
                    ...current,
                    phone: getPhoneError(form.phone) ?? undefined,
                  }))
                }
                className={`get-started-modal__phone${fieldErrors.phone ? ' get-started-modal__phone--invalid' : ''}`}
                numberInputProps={{
                  className: 'get-started-modal__phone-input',
                  inputMode: 'numeric',
                  autoComplete: 'tel',
                  placeholder: '10-digit phone number',
                }}
              />
              {fieldErrors.phone && (
                <div className="get-started-modal__field-error">{fieldErrors.phone}</div>
              )}
            </Form.Group>

            <Form.Group controlId="lead-hiring-goals">
              <Form.Label className="get-started-modal__label">
                Tell us a bit about your hiring goals<span aria-hidden>*</span>
              </Form.Label>
              <Form.Control
                className="get-started-modal__textarea"
                as="textarea"
                rows={4}
                required
                placeholder={hiringGoalsPlaceholder}
                value={form.hiringGoals}
                onChange={(event) => updateField('hiringGoals')(event.target.value)}
              />
            </Form.Group>
          </div>

          <Form.Check
            className="get-started-modal__opt-in"
            type="checkbox"
            id="lead-marketing-opt-in"
            label="Yes, I'd like to receive hiring insights and resources from SBR2TH."
            checked={form.marketingOptIn}
            onChange={(event) => updateField('marketingOptIn')(event.target.checked)}
          />

          <div className="get-started-modal__submit-wrap">
            <Button
              type="submit"
              className="get-started-modal__submit btn-gradient"
              disabled={submitting}
            >
              {submitting ? 'Submitting…' : 'Get Started'}
              {!submitting && (
                <SbrIcon icon={LuArrowRight} size={18} className="get-started-modal__submit-icon" />
              )}
            </Button>
          </div>

          <p className="get-started-modal__privacy mb-0">
            <SbrIcon icon={LuLock} size={14} className="get-started-modal__privacy-icon" />
            We respect your privacy. Your information is safe with us.
          </p>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
